import Swup from 'swup';

const MAX_STEP = 32;
const DURATION = 600;
const LOADER_DELAY = 800;
const LOADER_MIN = 500;
const BOOT_TEXT = 700;
const BOOT_STRIPES = 900;

// Ultimate Member carica CSS e JS solo nelle pagine con il form: serve un caricamento completo
const isFullLoad = (url) => /\/registrati\/?$/.test(url.split(/[?#]/)[0]);

const FILTER = `
<svg width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false">
  <filter id="pixelate" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
    <feFlood x="0" y="0" width="1" height="1"/>
    <feComposite width="2" height="2"/>
    <feTile result="tiles"/>
    <feComposite in="SourceGraphic" in2="tiles" operator="in"/>
    <feMorphology operator="dilate" radius="1"/>
  </filter>
</svg>`;

export default class PageTransitions {
  constructor({ onView } = {}) {
    document.body.insertAdjacentHTML('beforeend', FILTER);

    this.loader = document.querySelector('.page-loader');
    this.loaded = true;

    const filter = document.getElementById('pixelate');

    this.root = document.documentElement;
    this.flood = filter.querySelector('feFlood');
    this.tile = filter.querySelector('feComposite');
    this.dilate = filter.querySelector('feMorphology');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.swup = new Swup({
      containers: ['#site-header', '#main', '#site-footer'],
      ignoreVisit: isFullLoad,
    });
    this.swup.hooks.on('visit:start', () => {
      this.loaded = false;
      this.ready = new Promise((resolve) => {
        this.markLoaded = resolve;
      });
    });
    this.swup.hooks.replace('animation:out:await', async () => {
      await this.run(0, 1);
      await this.waitLoader();
    });
    this.swup.hooks.replace('animation:in:await', () => this.run(1, 0));
    this.swup.hooks.on('page:load', () => {
      this.loaded = true;
      this.markLoaded?.();
    });
    this.swup.hooks.on('visit:end', () => this.apply(0));
    this.swup.hooks.on('page:view', (visit) => {
      const nextBody = visit.to.document?.body;

      if (nextBody) {
        document.body.className = nextBody.className;
      }

      onView?.();
    });

    this.onExit = (e) => {
      const link = e.target.closest('a[href]');

      if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
        || link.target === '_blank' || link.origin !== location.origin || !isFullLoad(link.href)) {
        return;
      }

      e.preventDefault();
      this.run(0, 1).then(() => {
        location.href = link.href;
      });
    };
    document.addEventListener('click', this.onExit);

    if (this.loader.classList.contains('is-boot')) {
      this.boot();
    }
  }

  async waitLoader() {
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    await Promise.race([this.ready, wait(LOADER_DELAY)]);

    if (this.loaded) {
      return;
    }

    const shown = performance.now();

    this.loader.classList.add('is-active');

    await this.ready;
    await wait(Math.max(0, LOADER_MIN - (performance.now() - shown)));

    this.loader.classList.remove('is-active');
  }

  async boot() {
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const loaded = document.readyState === 'complete'
      ? Promise.resolve()
      : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

    await Promise.all([loaded, wait(BOOT_TEXT)]);

    if (!this.reduced.matches) {
      this.loader.classList.replace('is-boot', 'is-active');
      await wait(BOOT_STRIPES);
    }

    this.apply(1);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    this.loader.classList.remove('is-boot', 'is-active');
    await this.run(1, 0);
  }

  apply(amount) {
    const step = Math.round(amount ** 1.6 * MAX_STEP);

    if (step === 0) {
      this.root.style.removeProperty('--pixel-filter');
      this.root.style.removeProperty('--pixel-opacity');
      return;
    }

    this.flood.setAttribute('x', step);
    this.flood.setAttribute('y', step);
    this.tile.setAttribute('width', step * 2);
    this.tile.setAttribute('height', step * 2);
    this.dilate.setAttribute('radius', step);
    this.root.style.setProperty('--pixel-filter', 'url(#pixelate)');
    this.root.style.setProperty('--pixel-opacity', Math.max(0, 1 - Math.max(0, amount - 0.5) * 2));
  }

  run(from, to) {
    if (this.reduced.matches) {
      return Promise.resolve();
    }

    const start = performance.now();

    return new Promise((resolve) => {
      const frame = () => {
        const t = Math.min((performance.now() - start) / DURATION, 1);

        this.apply(from + (to - from) * t);

        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          resolve();
        }
      };

      this.apply(from);
      requestAnimationFrame(frame);
    });
  }

  destroy() {
    document.removeEventListener('click', this.onExit);
    this.swup.destroy();
    this.loader.remove();
    this.apply(0);
  }
}
